import { Box } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Shown_DashProfilePage } from "@mirage/analytics/events/types/shown_dash_profile_page";
import { useUserProfileApi } from "@mirage/mosaics/UserProfilePage/hooks";
import { UserProfilePageLayout } from "@mirage/mosaics/UserProfilePage/UserProfilePageLayout";
import { canUserEditUserProfile } from "@mirage/mosaics/UserProfilePage/utils";
import useDropboxAccount, {
  useIsDropboxer,
} from "@mirage/service-auth/useDropboxAccount";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { usePersonFromOrgChartByDisplayHandle } from "@mirage/service-people/hooks";
import { openURL } from "@mirage/service-platform-actions";
import { DashSpinner } from "@mirage/shared/dash-spinner";
import { useDashTitle } from "@mirage/shared/hooks/DashTitle";
import { NotFound } from "@mirage/webapp/pages/NotFound";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

export const COMPANY_OS_FALLBACK_PEOPLE_URL =
  "https://os.dropbox.com/people?s={{q}}";

export const UserProfilePage = () => {
  const location = useLocation();
  const displayHandle = location.pathname.split("/")[2];
  const isEdit = location.pathname.includes("/edit");
  const { reportPapEvent } = useMirageAnalyticsContext();

  // Fetch full user profile info to render page but
  // use basic user info from org chart cache as source of truth (for consistency across pages)
  const userProfileInfo = useUserProfileApi({ displayHandle });
  const basicPerson = usePersonFromOrgChartByDisplayHandle(displayHandle);
  const userInfo =
    userProfileInfo === null
      ? null
      : userProfileInfo
        ? {
            ...userProfileInfo,
            ...(basicPerson?.accountId && {
              account_id: basicPerson.accountId,
              display_handle: basicPerson.displayHandle,
              name: basicPerson.displayName,
              email: basicPerson.email,
              profile_photo_url: basicPerson.profilePhotoUrl,
              role: basicPerson.role,
            }),
          }
        : undefined;

  useDashTitle(userInfo?.name ?? "");

  useEffect(() => {
    if (displayHandle) {
      reportPapEvent(
        PAP_Shown_DashProfilePage({
          dashActionSurface: "dash_profile_page",
          featureLine: "people",
        }),
      );
    }
  }, [reportPapEvent, displayHandle]);

  const currentAccount = useDropboxAccount();
  const isDropboxer = useIsDropboxer();

  const redirectToCompanyOS = useCallback(() => {
    const peopleUrl = COMPANY_OS_FALLBACK_PEOPLE_URL.replace(
      "{{q}}",
      displayHandle,
    );
    if (EnvCtx?.surface === "desktop") {
      openURL(peopleUrl);
    } else {
      window.location.href = peopleUrl;
    }
  }, [displayHandle]);

  const isUserNotFound = userInfo === null;
  useEffect(() => {
    if (!displayHandle || !isDropboxer) {
      return; // do not redirect to CompanyOS unless displayHandle is set and user is a Dropboxer
    }

    // We updated /who shortcut to redirect to User Profile page but Dash People is not rolled out on desktop yet.
    // We should redirect to CompanyOS people URL as fallback, but remove this check once desktop is fully rolled out.
    const isDesktop = EnvCtx.surface == "desktop";
    if (isDesktop) {
      redirectToCompanyOS();
    }

    // If user is not found, also redirect to CompanyOS as fallback
    // since we don't support showing multiple people on SERP yet
    if (isUserNotFound) {
      redirectToCompanyOS();
    }
  }, [isDropboxer, displayHandle, isUserNotFound]);

  if (!displayHandle || isUserNotFound) {
    return <NotFound />;
  }

  // Only allow current user to edit their own profile
  if (isEdit && userInfo) {
    const canEdit = canUserEditUserProfile(
      currentAccount?.email,
      userInfo.email,
    );
    if (!canEdit) {
      return <NotFound />;
    }
  }

  if (!userInfo) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <DashSpinner />
      </Box>
    );
  }

  return <UserProfilePageLayout user={userInfo} isEdit={isEdit} />;
};
